import React, { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import useFirmSelectionStore, {
  useFirmSelectionState,
  useFirmSelectionActions
} from '../../../../../store/firmSelectionStore'
import Typography from '@mui/material/Typography'
import { Tooltip, Link, useTheme, Checkbox, Box } from '@mui/material'
import { ExpandMore, ExpandLess } from '@mui/icons-material'
import classnames from 'classnames'
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
  getFilteredRowModel,
  getSortedRowModel
} from '@tanstack/react-table'
import {
  TablePaginationComponentSemiClientSide
} from '../molecules/TablePaginationComponent'
import Flag from '../atoms/Flag'
import LoadingOverlay from '../atoms/LoadingOverlay'
import { countryEN } from '../../../../../assets/data/country-en'
import tableStyles from '../../styles/table.module.scss'

const httpPrefixer = url => (url?.indexOf('://') === -1 ? `http://${url}` : url)

const getCountryName = (countryCode) => {
  const country = countryEN.find(c => c.countryCode === countryCode)
  return country ? country.countryName : countryCode
}

const columnHelper = createColumnHelper()

const FirmListSemiClientSide = ({
  firmKey,
  data,
  setSelectedFirms,
  fetching,
  fuzzyFilter,
  globalFilter,
  setGlobalFilter,
  pageSize,
  onPageChange,
  onPageSizeChange,
  disableInteraction = false,
  hideWebsiteColumn = false
}) => {
  const { t } = useTranslation()
  const theme = useTheme()

  const { selectionData } = useFirmSelectionState(firmKey)

  const { handleRowToggle } = useFirmSelectionActions()
  const isRowSelected = useFirmSelectionStore((state) => state.isRowSelected)

  useEffect(() => { if (setSelectedFirms) setSelectedFirms(selectionData) },
    [selectionData, setSelectedFirms])

  const columns = useMemo(() => {
    const getFirmData = (firmObject) => {
      if (firmObject.firmType === 'P7' && firmObject.p7Firm) {
        return firmObject.p7Firm
      }
      if (firmObject.firmType === 'TREG' && firmObject.tregFirm) {
        return firmObject.tregFirm
      }
      return firmObject
    }

    const base = [
      columnHelper.display({
        id: 'select',
        header: () => null,
        cell: ({ row }) => (
          <Checkbox
            checked={isRowSelected(firmKey, row.original)}
            onChange={(e) => {
              e.stopPropagation()
              handleRowToggle(firmKey, row.original, !isRowSelected(firmKey, row.original))
            }}
            disabled={disableInteraction}
            size="small"
            onClick={(e) => e.stopPropagation()}
          />
        ),
        size: 50
      }),
      columnHelper.accessor('name', {
        header: t('mailTemplate.wizard.firmList.firmName'),
        cell: ({ row }) => {
          const firmData = getFirmData(row.original)
          const firmName = firmData.name
          const website = firmData.web

          const nameContent = (
            <span style={{
              display: 'block',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              maxWidth: '300px'
            }}>
              {firmName}
            </span>
          )
          return (
            <Typography color="text.primary" className="font-medium">
              <Tooltip title={firmName} arrow placement="top"
                       disableHoverListener={firmName.length <= 40}>
                {hideWebsiteColumn && website
                  ? (
                    <Link
                      href={httpPrefixer(website)}
                      target="_blank"
                      rel="noreferrer"
                      onClick={e => e.stopPropagation()}
                      underline="hover"
                      sx={{ color: `${theme.palette.text.primary} !important` }}
                    >
                      {nameContent}
                    </Link>
                    )
                  : nameContent}
              </Tooltip>
            </Typography>
          )
        }
      })
    ]

    if (!hideWebsiteColumn) {
      base.push(
        columnHelper.accessor('web', {
          header: t('mailTemplate.wizard.firmList.website'),
          cell: ({ row }) => {
            const firmData = getFirmData(row.original)

            return (
              <a href={httpPrefixer(firmData.web)} target="_blank"
                 rel="noreferrer">
                <Typography color="text.primary" className="font-medium" noWrap>
                  {firmData.web}
                </Typography>
              </a>
            )
          }
        })
      )
    }

    base.push(
      columnHelper.accessor('emailList', {
        header: t('mailTemplate.wizard.firmList.email'),
        cell: ({ row }) => {
          const firmData = getFirmData(row.original)
          const emailList = firmData.emailList

          return emailList && emailList.length > 1
            ? (
              <Tooltip
                title={<div
                  style={{ whiteSpace: 'pre-line' }}>{emailList.join(
                    '\n')}</div>}
                arrow placement="top"
              >
              <span style={{
                maxWidth: '200px',
                display: 'inline-block',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>
                {emailList[0]}
              </span>
              </Tooltip>
              )
            : (
              <span>{emailList?.[0] || '-'}</span>
              )
        }
      })
    )

    if (firmKey !== 'rfFirms' && firmKey !== 'spFirms') {
      base.push(
        columnHelper.accessor('import.countries', {
          header: t('mailTemplate.wizard.firmList.importCountries'),
          cell: ({ row }) => {
            const firmData = getFirmData(row.original)
            const countries = firmData.import?.countries || []

            if (countries.length === 0) {
              return <Typography variant="body2" style={{ color: 'rgba(158, 158, 158, 1)' }}>—</Typography>
            }

            return (
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
                {countries.map((countryCode, index) => (
                  <Tooltip key={index} title={getCountryName(countryCode)} arrow placement="top">
                    <span>
                      <Flag code={countryCode} style={{ fontSize: '20px' }} />
                    </span>
                  </Tooltip>
                ))}
              </Box>
            )
          }
        }),
        columnHelper.accessor('export.countries', {
          header: t('mailTemplate.wizard.firmList.exportCountries'),
          cell: ({ row }) => {
            const firmData = getFirmData(row.original)
            const countries = firmData.export?.countries || []

            if (countries.length === 0) {
              return <Typography variant="body2" style={{ color: 'rgba(158, 158, 158, 1)' }}>—</Typography>
            }

            return (
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
                {countries.map((countryCode, index) => (
                  <Tooltip key={index} title={getCountryName(countryCode)} arrow placement="top">
                    <span>
                      <Flag code={countryCode} style={{ fontSize: '20px' }} />
                    </span>
                  </Tooltip>
                ))}
              </Box>
            )
          }
        })
      )
    }

    return base
  }, [
    t,
    hideWebsiteColumn,
    theme.palette.text.primary,
    isRowSelected,
    handleRowToggle,
    firmKey,
    disableInteraction
  ])

  const table = useReactTable({
    data: data?.firms || [],
    columns,
    state: {
      globalFilter,
      pagination: { pageIndex: data?.page || 0, pageSize }
    },
    manualPagination: true,
    pageCount: data?.pageCount || 0,
    totalRecordCount: data?.totalRecordCount || 0,
    globalFilterFn: fuzzyFilter,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onGlobalFilterChange: setGlobalFilter,
    onPaginationChange: updater => {
      const current = { pageIndex: data?.page || 0, pageSize }
      const next = typeof updater === 'function' ? updater(current) : updater
      if (next.pageIndex !== current.pageIndex) onPageChange?.(next.pageIndex)
      if (next.pageSize !== current.pageSize) onPageSizeChange?.(next.pageSize)
    }
  })

  useEffect(() => { table.setPageSize(pageSize) }, [pageSize])

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      minHeight: '400px',
      height: '100%'
    }}>
      <div style={{
        flex: 1,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative'
      }}>
        {fetching && <LoadingOverlay />}
        <div className="overflow-x-auto" style={{ flex: 1 }}>
          <table className={tableStyles.table}>
            <thead>
            {table.getHeaderGroups().map(hg => (
              <tr key={hg.id}>
                {hg.headers.map(header => (
                  <th key={header.id}>
                    {header.isPlaceholder
                      ? null
                      : (
                        <div
                          className={classnames({
                            'flex items-center': header.column.getIsSorted(),
                            'cursor-pointer select-none': header.column.getCanSort()
                          })}
                          onClick={header.column.getToggleSortingHandler()}
                        >
                          {flexRender(header.column.columnDef.header,
                            header.getContext())}
                          {{
                            asc: <ExpandLess/>,
                            desc: <ExpandMore/>
                          }[header.column.getIsSorted()] ?? null}
                        </div>
                        )}
                  </th>
                ))}
              </tr>
            ))}
            </thead>

            <tbody>
            {table.getRowModel().rows.map(row => (
              <tr
                key={row.id}
                className={classnames({ selected: isRowSelected(firmKey, row.original) })}
              >
                {row.getVisibleCells().map(cell => (
                  <td key={cell.id}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
            {table.getRowModel().rows.length === 0 && !fetching && (
              <tr>
                <td colSpan={columns.length} className="text-center">
                  {t('mailTemplate.wizard.firmList.noData')}
                </td>
              </tr>
            )}
            </tbody>
          </table>
        </div>

        <div className="px-4 py-2">
          <TablePaginationComponentSemiClientSide
            table={table}
            infoText1={t('mailTemplate.wizard.firmList.tableInfo_1')}
            infoText2={t('mailTemplate.wizard.firmList.tableInfo_2')}
          />
        </div>
      </div>
    </div>
  )
}

export default FirmListSemiClientSide
